// ── Portal Intelligence Engine ──
// Computes "What Matters Now" insights, priority signals,
// and readiness data from core job state. Read-only overlay.

import type { Job, Issue } from "@/data/types";
import type { ChangeOrder } from "@/data/changeOrderTypes";
import type { BuildReviewEvent } from "@/data/buildReviewTypes";
import { deriveReadinessFromJob, type CanonicalReadinessStatus } from "@/data/readiness";

export type ConfidenceLevel = "high" | "medium" | "low";
export type UrgencyLevel = "critical" | "attention" | "info" | "none";

export interface WhatMattersNow {
  currentStatus: string;
  nextStep: string;
  blockingIssues: string[];
  confirmationsRequired: PortalAction[];
  confidence: ConfidenceLevel;
  confidenceReasons: string[];
  urgency: UrgencyLevel;
  attentionFlags: string[];
}

export interface PortalAction {
  id: string;
  type: "confirm_appointment" | "approve_change_order" | "pay_invoice" | "submit_issue" | "upload_photos";
  label: string;
  description: string;
  urgency: UrgencyLevel;
  data?: Record<string, unknown>;
}

export interface PortalEvent {
  id: string;
  jobId: string;
  type: string;
  description: string;
  timestamp: string;
  actor: "customer" | "system";
  metadata?: Record<string, unknown>;
}

export interface SmartTimelineStep {
  stage: string;
  label: string;
  description: string;
  completed: boolean;
  active: boolean;
  timestamp: string | null;
  risk: string | null;
  readinessStatus: "ready" | "at_risk" | "blocked" | null;
  confirmation: string | null;
  crewAssignment: string | null;
}

export interface TrustInfo {
  teamAssigned: { name: string; role: string }[];
  proofOfWork: { type: "photo" | "qa"; label: string; date: string }[];
  documents: { name: string; status: string; date: string }[];
  warrantyInfo: { type: string; duration: string; expires: string } | null;
}

// ── Status derivation ──

function deriveCustomerStatus(job: Job): string {
  const stage = job.currentStage;
  if (stage === "Completed") return "Project Complete";
  if (stage === "QA Review") return "Quality Review in Progress";
  if (stage === "In Progress") {
    // Check for continuation status from wrap records
    return "Installation In Progress";
  }
  if (stage === "Assigned and Dispatched" || stage === "Ready for Dispatch") return "Team Assigned — Ready Soon";
  if (stage === "Scheduled Confirmed") return "Installation Scheduled";
  if (stage === "Scheduled Tentative") return "Scheduling in Progress";
  if (stage === "Ordered" || stage === "Permit and Order Pending") return "Materials Being Prepared";
  if (stage === "Ready for Ordering" || stage === "Review Complete") return "Project Planning";
  return "Project Confirmed";
}

function deriveNextStep(job: Job, openIssues: Issue[]): string {
  if (openIssues.some(i => i.severity === "Urgent")) return "We're resolving a priority issue on your project.";
  const stage = job.currentStage;
  if (stage === "Completed") return "Your project is complete. We're here if you need anything.";
  if (stage === "QA Review") {
    // Surface payment status in next step messaging
    const hasPendingPayment = job.financials?.paymentPlan?.lineItems.some(
      li => li.dueAt === "post_install" && li.status !== "paid" && li.status !== "waived"
    );
    if (hasPendingPayment) return "Final payment pending — our team will follow up.";
    return "Final payment collected — project entering final review.";
  }
  if (stage === "In Progress") return "Installation is underway. We'll update you when complete.";
  if (stage === "Scheduled Confirmed" || stage === "Scheduled Tentative")
    return job.scheduledInstallDate
      ? `Installation is scheduled for ${new Date(job.scheduledInstallDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}.`
      : "We're finalizing your installation date.";
  if (stage === "Ordered" || stage === "Permit and Order Pending") return "Materials are being ordered and prepared for your project.";
  return "We're preparing everything for your project.";
}

function computeConfidence(job: Job, openIssues: Issue[]): { level: ConfidenceLevel; reasons: string[] } {
  const reasons: string[] = [];
  let score = 100;

  if (openIssues.some(i => i.severity === "Urgent")) { score -= 30; reasons.push("Priority issue being resolved"); }
  if (openIssues.length > 2) { score -= 15; reasons.push("Multiple open items"); }
  if (job.scheduledInstallDate && new Date(job.scheduledInstallDate) < new Date()) {
    score -= 20; reasons.push("Scheduled date has passed");
  }
  if (job.dispatchStatus === "Rejected") { score -= 25; reasons.push("Team reassignment in progress"); }
  if (job.materialReadiness === "Missing Items" || job.materialReadiness === "Field Purchase Required") { score -= 15; reasons.push("Materials delayed"); }

  if (score >= 80) return { level: "high", reasons: reasons.length ? reasons : ["Everything is on track"] };
  if (score >= 50) return { level: "medium", reasons };
  return { level: "low", reasons };
}

function computeUrgency(actions: PortalAction[], blockingIssues: string[]): UrgencyLevel {
  if (blockingIssues.length > 0) return "critical";
  if (actions.some(a => a.urgency === "critical")) return "critical";
  if (actions.some(a => a.urgency === "attention")) return "attention";
  if (actions.length > 0) return "info";
  return "none";
}

// ── Main computation ──

export function computeWhatMattersNow(
  job: Job,
  openIssues: Issue[],
  changeOrders: ChangeOrder[] = []
): WhatMattersNow {
  const currentStatus = deriveCustomerStatus(job);
  const nextStep = deriveNextStep(job, openIssues);
  const blockingIssues = openIssues
    .filter(i => i.severity === "Urgent")
    .map(i => i.description?.slice(0, 80) || "Issue requires attention");

  const confirmationsRequired: PortalAction[] = [];

  // Pending appointment confirmation
  if (job.scheduledInstallDate && job.currentStage === "Scheduled Tentative") {
    confirmationsRequired.push({
      id: `action-confirm-appt-${job.id}`,
      type: "confirm_appointment",
      label: "Confirm Appointment",
      description: `Please confirm your installation on ${new Date(job.scheduledInstallDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}`,
      urgency: "attention",
      data: { date: job.scheduledInstallDate },
    });
  }

  // Pending change orders
  const pendingCOs = changeOrders.filter(co => co.status === "Pending Review");
  pendingCOs.forEach(co => {
    confirmationsRequired.push({
      id: `action-co-${co.id}`,
      type: "approve_change_order",
      label: "Review Change Order",
      description: `Change order: ${co.summary?.slice(0, 60) || "Project modification"} — $${Math.abs(co.changeAmount || 0).toLocaleString()}`,
      urgency: "attention",
      data: { changeOrderId: co.id, delta: co.changeAmount },
    });
  });

  // Payment needed — check actual line item status
  const postInstallItems = job.financials?.paymentPlan?.lineItems.filter(
    li => li.dueAt === "post_install"
  ) ?? [];
  const hasPendingPayment = postInstallItems.some(li => li.status !== "paid" && li.status !== "waived");
  const allPostPaid = postInstallItems.length > 0 && postInstallItems.every(li => li.status === "paid" || li.status === "waived");

  if (hasPendingPayment && (job.currentStage === "QA Review" || job.currentStage === "Completed")) {
    const pendingAmount = postInstallItems.filter(li => li.status !== "paid" && li.status !== "waived").reduce((s, li) => s + li.amount, 0);
    confirmationsRequired.push({
      id: `action-pay-${job.id}`,
      type: "pay_invoice",
      label: "Final Payment Due",
      description: `$${pendingAmount.toLocaleString()} due at project completion — our team will follow up`,
      urgency: "info",
      data: { balance: pendingAmount },
    });
  } else if (job.financials?.paymentStatus === "Invoiced" || job.financials?.paymentStatus === "Partial Payment") {
    const balance = job.financials.contractValue || 0;
    if (balance > 0) {
      confirmationsRequired.push({
        id: `action-pay-${job.id}`,
        type: "pay_invoice",
        label: "View Payment",
        description: `Balance due: $${balance.toLocaleString()}`,
        urgency: "info",
        data: { balance },
      });
    }
  }

  const { level: confidence, reasons: confidenceReasons } = computeConfidence(job, openIssues);
  const attentionFlags = [
    ...blockingIssues.map(() => "Issue requires attention"),
    ...(confirmationsRequired.length > 0 ? ["Action required from you"] : []),
  ];
  const urgency = computeUrgency(confirmationsRequired, blockingIssues);

  return {
    currentStatus,
    nextStep,
    blockingIssues,
    confirmationsRequired,
    confidence,
    confidenceReasons,
    urgency,
    attentionFlags: [...new Set(attentionFlags)],
  };
}

// ── Smart Timeline ──

export function computeSmartTimeline(job: Job, openIssues: Issue[]): SmartTimelineStep[] {
  const stageOrder: Record<string, number> = {
    "Sale Closed": 10, "Review Complete": 25, "Ready for Ordering": 30,
    "Ordered": 40, "Permit and Order Pending": 35, "Ready for Scheduling": 50,
    "Scheduled Tentative": 55, "Scheduled Confirmed": 60, "Ready for Dispatch": 65,
    "Assigned and Dispatched": 75, "In Progress": 80, "QA Review": 90, "Completed": 100,
  };
  const order = stageOrder[job.currentStage] ?? 0;

  const materialRisk = (job.materialReadiness === "Missing Items" || job.materialReadiness === "Field Purchase Required") ? "Materials delayed — we're working on it" : null;
  const permitRisk = job.permitStatus === "Submitted" && order >= 30 ? "Permit approval pending" : null;

  return [
    {
      stage: "booked", label: "Project Confirmed", description: "Your project is officially booked.",
      completed: order >= 10, active: order >= 10 && order < 30, timestamp: job.contractDocument?.approvedAt || null,
      risk: null, readinessStatus: order >= 10 ? "ready" : null, confirmation: null, crewAssignment: null,
    },
    {
      stage: "planning", label: "Planning & Preparation", description: "Finalizing materials, permits, and scheduling.",
      completed: order >= 50, active: order >= 30 && order < 50, timestamp: null,
      risk: materialRisk || permitRisk, readinessStatus: materialRisk || permitRisk ? "at_risk" : order >= 30 ? "ready" : null,
      confirmation: null, crewAssignment: null,
    },
    {
      stage: "scheduled", label: "Installation Scheduled",
      description: job.scheduledInstallDate
        ? `Scheduled for ${new Date(job.scheduledInstallDate).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}`
        : "Date will be confirmed soon.",
      completed: order >= 60 && !!job.scheduledInstallDate, active: order >= 50 && order < 75,
      timestamp: job.scheduledInstallDate || null,
      risk: job.scheduledInstallDate && new Date(job.scheduledInstallDate) < new Date() && order < 80 ? "Date may need updating" : null,
      readinessStatus: order >= 60 ? "ready" : order >= 50 ? "at_risk" : null,
      confirmation: job.currentStage === "Scheduled Tentative" ? "Please confirm your appointment" : null,
      crewAssignment: job.assignedSubcontractor || null,
    },
    {
      stage: "in_progress", label: "Installation", description: "Our team is on-site completing your project.",
      completed: order >= 90, active: order >= 80 && order < 90, timestamp: null,
      risk: openIssues.some(i => i.severity === "Urgent") ? "Issue reported — being resolved" : null,
      readinessStatus: order >= 80 ? (openIssues.some(i => i.severity === "Urgent") ? "at_risk" : "ready") : null,
      confirmation: null, crewAssignment: job.assignedSubcontractor || null,
    },
    {
      stage: "quality", label: "Quality Review", description: "Final inspection to ensure everything meets standards.",
      completed: order >= 100, active: order >= 90 && order < 100, timestamp: null,
      risk: null, readinessStatus: order >= 90 ? "ready" : null, confirmation: null, crewAssignment: null,
    },
    {
      stage: "completed", label: "Complete", description: "Your project is finished. Welcome to your new space!",
      completed: order >= 100, active: order === 100, timestamp: null,
      risk: null, readinessStatus: order >= 100 ? "ready" : null, confirmation: null, crewAssignment: null,
    },
  ];
}

// ── Trust Info ──

export function computeTrustInfo(job: Job, openIssues: Issue[]): TrustInfo {
  const teamAssigned: TrustInfo["teamAssigned"] = [];
  if (job.assignedSubcontractor) teamAssigned.push({ name: job.assignedSubcontractor, role: "Lead Installer" });
  if (job.salesRep) teamAssigned.push({ name: job.salesRep, role: "Project Advisor" });

  const proofOfWork: TrustInfo["proofOfWork"] = [];
  if (job.qaSubmitted) {
    proofOfWork.push({ type: "qa", label: "Quality Inspection Submitted", date: new Date().toISOString() });
  }
  if (job.photos && job.photos.length > 0) {
    proofOfWork.push({ type: "photo", label: "Installation Photos Available", date: new Date().toISOString() });
  }

  const documents: TrustInfo["documents"] = [];
  if (job.contractDocument?.status === "Signed") {
    documents.push({ name: "Signed Contract", status: "Complete", date: job.contractDocument.approvedAt || "" });
  }
  if (job.changeOrders?.length) {
    job.changeOrders.forEach((co, i) => {
      documents.push({ name: `Change Order #${i + 1}`, status: co.status, date: co.signedAt || co.createdAt });
    });
  }

  const warrantyInfo = job.currentStage === "Completed"
    ? { type: "Standard Warranty", duration: "1 Year", expires: new Date(Date.now() + 365 * 86400000).toISOString().split("T")[0] }
    : null;

  return { teamAssigned, proofOfWork, documents, warrantyInfo };
}

// ── Project Readiness ──

export type ReadinessItemStatus = "ready" | "pending" | "at_risk" | "blocked";

export interface ReadinessItem {
  key: string;
  label: string;
  status: ReadinessItemStatus;
  displayText: string;
  description?: string;
}

export interface ProjectReadiness {
  items: ReadinessItem[];
  summary: string;
  overallStatus: ReadinessItemStatus;
}

// Portal readiness now delegates to the canonical derivation layer.
// This ensures portal, dashboards, and automation share the same truth.

function canonicalToPortalStatus(s: CanonicalReadinessStatus): ReadinessItemStatus {
  return s; // Types are identical by design
}

export function computeProjectReadiness(
  job: Job,
  buildReviewEvents: BuildReviewEvent[] = [],
): ProjectReadiness {
  const derived = deriveReadinessFromJob(job, buildReviewEvents);
  const categories = [derived.permit, derived.materials, derived.crew, derived.schedule, derived.site, derived.financial, derived.buildReview];

  const items: ReadinessItem[] = categories.map(c => ({
    key: c.category,
    label: c.label,
    status: canonicalToPortalStatus(c.status),
    displayText: c.shortText,
    description: c.description,
  }));

  const blockedOrRisk = items.filter(i => i.status === "blocked" || i.status === "at_risk");
  const pending = items.filter(i => i.status === "pending");

  const overallStatus = canonicalToPortalStatus(derived.overall);

  let summary: string;
  if (overallStatus === "ready") {
    summary = "Your project is fully ready — all dependencies are clear.";
  } else {
    const names = [...blockedOrRisk, ...pending].map(i => i.label.toLowerCase());
    if (names.length === 1) {
      summary = `Your project is on track except for ${names[0]}.`;
    } else {
      const last = names.pop();
      summary = `Your project is on track except for ${names.join(", ")} and ${last}.`;
    }
  }

  return { items, summary, overallStatus };
}

// ── Event Builder ──

let _eventCounter = 0;

export function createPortalEvent(
  jobId: string,
  type: string,
  description: string,
  actor: "customer" | "system" = "customer",
  metadata?: Record<string, unknown>
): PortalEvent {
  _eventCounter++;
  return {
    id: `pe-${Date.now().toString(36)}-${_eventCounter}`,
    jobId,
    type,
    description,
    timestamp: new Date().toISOString(),
    actor,
    metadata,
  };
}
